import { Injectable, inject } from '@angular/core';
import { PacketFileService } from '@escrowtab/packets-api-client';
import { Observable, Subject, map, of, takeUntil, tap, throwError } from 'rxjs';

/**
 * Caches the packet PDF for the current eJournal session.
 * Prefer a caller-supplied File (e.g. dashboard modal); otherwise fetch signed/unsigned from the API.
 */
@Injectable()
export class EjournalPacketPdfService {
  private readonly packetFileService = inject(PacketFileService);
  private readonly cancelLoad$ = new Subject<void>();

  private cachedFile: File | null = null;
  private cachedPacketId: string | null = null;

  /**
   * Stores a caller-supplied packet PDF (skips network fetch on cache hit).
   */
  setPacketFile(file: File, packetId: string): void {
    this.cachedFile = file;
    this.cachedPacketId = packetId;
  }

  /**
   * Returns the cached packet PDF when it matches `packetId`, otherwise fetches
   * `signed.pdf` or `unsigned.pdf` and caches the result.
   */
  getPacketFile(packetId: string, signed: boolean): Observable<File> {
    if (!packetId) {
      return throwError(() => new Error('Packet id is required'));
    }

    if (this.cachedFile && this.cachedPacketId === packetId) {
      return of(this.cachedFile);
    }

    const fileName = signed ? 'signed.pdf' : 'unsigned.pdf';
    return this.packetFileService.getPacketFile({ packetId, fileName }).pipe(
      takeUntil(this.cancelLoad$),
      map(
        (data) =>
          new File([data], fileName, {
            type: 'application/pdf',
          }),
      ),
      tap((file) => {
        this.cachedFile = file;
        this.cachedPacketId = packetId;
      }),
    );
  }

  /**
   * Drops the cached file and cancels any in-flight load.
   */
  clear(): void {
    this.cancelLoad$.next();
    this.cachedFile = null;
    this.cachedPacketId = null;
  }
}
